import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import { CatalogManager, ProjectInfoRecord, openCatalogDirectory } from "@/lib/catalog"
import { StyleRecord } from "@/storage/indexeddb"
import { replaceDialog } from "../dialogs-state"
import { chooseProjectMode, fetchOrCreateProject, resetProjectState, startEditing } from "./project"
import { handleProjectError } from "@/store/project/handle-project-error"
import { ResetProjectDialog } from "@/components/Dialogs/ProjectResetDialog"
import toast from "react-hot-toast"
import { setUpProject, startMetadataPrefetching } from "./setup-project"
import { StyleResponseDTO } from "@/api/dto/styles"
import { markSessionOpening, navigateToProjectRoute, openSession } from "@/routing/project-session"
import { FilesInfo, getFiles } from "@/storage/file-system"
import { allSupportedExtensions } from "@/io/formats/supported-extensions"
import { LoadingDialog } from "@/components/Dialogs/LoadingDialog"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"

export interface NewStyleSetupOptions {
	isExpressSetup: boolean
}

export function createProject(
	folder: FileSystemDirectoryHandle,
	style: StyleRecord | null = null,
	styleDTO?: StyleResponseDTO,
	newStyleSetup?: NewStyleSetupOptions,
) {
	const handleOpenExisting = async (projectInfo: ProjectInfoRecord) => {
		try {
			const { project, projectInfo: updatedProjectInfo } = await fetchOrCreateProject(
				folder,
				projectInfo,
			)
			await openSession({ project, existingProjectInfo: updatedProjectInfo })
		} catch (error) {
			void handleProjectError(error, { source: "create_project_keep_existing" })
		}
	}

	const handleCreateProject = async (filesInfo?: FilesInfo) => {
		const project = (await fetchOrCreateProject(folder)).project
		markSessionOpening(project.id)
		await startMetadataPrefetching(project, folder, filesInfo)
		//Open project view first for better perceived performance, then set up project and style
		await navigateToProjectRoute(project.id)

		if (newStyleSetup) {
			replaceDialog(LoadingDialog, { title: i18n.t("store:createProject.creatingProject") })
			await resetProjectState("editing", project.id)
			await setUpProject({
				handle: folder,
				selectedStyle: "new",
				needsSetup: true,
				isExpressSetup: newStyleSetup.isExpressSetup,
				existingProject: project,
			})
			return
		}

		if (style) {
			// If a style is provided, we can assume user will be editing
			void startEditing({
				handle: folder,
				style,
				disableClose: false,
				existingProject: project,
			})
		} else {
			chooseProjectMode({
				handle: folder,
				disableClose: false,
				existingProject: project,
				styleDTO,
			})
		}
	}

	const createProject = async () => {
		try {
			const filesInfo = await getFiles(folder, null, {
				allowedExtensions: allSupportedExtensions,
			})
			const nestedCatalogPath = filesInfo.nestedCatalogPath
			if (nestedCatalogPath) {
				replaceDialog(ConfirmDialog, {
					trackingViewName: "nested_catalog_in_subfolder_dialog",
					title: i18n.t("store:createProject.nestedCatalogFoundTitle"),
					message: i18n.t("store:createProject.nestedCatalogFoundMessage", {
						path: nestedCatalogPath,
					}),
					primary: { title: i18n.t("common:action.ok") },
				})
				return
			}

			await openCatalogDirectory(folder)
			const currentProjectInfo = await CatalogManager.fetchProjectInfo()

			if (currentProjectInfo) {
				//Catalog exists
				replaceDialog(ConfirmDialog, {
					title: i18n.t("store:createProject.existingCatalogTitle"),
					message: i18n.t("store:createProject.existingCatalogMessage"),
					primary: {
						title: i18n.t("store:createProject.continueEditing"),
						handler: () => handleOpenExisting(currentProjectInfo),
					},
					secondary: {
						title: i18n.t("store:createProject.resetProject"),
						isDestructive: true,
						disablePopDialog: true,
						handler: () => {
							replaceDialog(ResetProjectDialog, {
								projectInfo: currentProjectInfo,
								onFinish: (selected) => {
									if (selected.has("edits")) {
										handleCreateProject(filesInfo)
									} else if (selected.has("ratings")) {
										// If only ratings are reset, we can keep the existing catalog and edits
										toast.success(i18n.t("store:createProject.ratingsReset"))
										void handleOpenExisting(currentProjectInfo)
										//If only ratings are reset, we will open the project ignoring selected style?
									}
								},
							})
						},
					},
					canClose: true,
					trackingViewName: "existing_project_dialog",
				})
			} else {
				void handleCreateProject(filesInfo)
			}
		} catch (error) {
			handleProjectError(error, { source: "create_project" })
		}
	}

	//Wait a bit so the permission dialog shows checked
	setTimeout(createProject, 500)
}
